import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, limit, getDocs, addDoc, updateDoc, getDoc ,doc, serverTimestamp, getFirestore } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth'; 
import PhoneInput from '../components/ui/PhoneInput';
import SmsComplianceCheckboxes from '../components/ui/SmsComplianceCheckboxes';
import SEO from '../components/shared/SEO';

// Utility for dynamic font sizing
export const getSizingClasses = (textSize) => {
    switch(textSize) {
        case 'small': return { 
            heroHeading: 'text-4xl md:text-5xl lg:text-6xl', heroSub: 'text-base md:text-xl', heading: 'text-3xl md:text-4xl', body: 'text-base', small: 'text-xs', cardHeading: 'text-lg md:text-xl' 
        };
        case 'large': return { 
            heroHeading: 'text-6xl md:text-8xl lg:text-9xl', heroSub: 'text-xl md:text-3xl', heading: 'text-5xl md:text-6xl', body: 'text-xl md:text-2xl', small: 'text-base', cardHeading: 'text-3xl md:text-4xl' 
        };
        case 'xlarge': return { 
            heroHeading: 'text-7xl md:text-9xl lg:text-[10rem]', heroSub: 'text-2xl md:text-4xl', heading: 'text-6xl md:text-7xl', body: 'text-2xl md:text-3xl', small: 'text-lg', cardHeading: 'text-4xl md:text-5xl' 
        };
        default: return { 
            heroHeading: 'text-5xl md:text-7xl lg:text-8xl', heroSub: 'text-lg md:text-2xl', heading: 'text-4xl md:text-5xl', body: 'text-lg', small: 'text-sm', cardHeading: 'text-2xl md:text-3xl' 
        };
    }
};

export function CashFundContributionModal({ fund, theme, onClose, weddingId, db }) {
    const [amount, setAmount] = useState('');
    const [guestName, setGuestName] = useState('');
    const [message, setMessage] = useState('');
    const [step, setStep] = useState('details'); 
    
    const presetAmounts = [50, 100, 150, 200];
    const themeColor = theme?.primaryColor || '#ec4899';

    const handleContribute = async (e) => {
        e.preventDefault();
        const numAmount = parseFloat(amount);
        if (!guestName || isNaN(numAmount) || numAmount <= 0) return;

        setStep('processing');
        
        try {
            const weddingRef = doc(db, 'weddings', weddingId);
            const snap = await getDoc(weddingRef);
            if (snap.exists()) {
                const data = snap.data();
                const newSections = data.sections.map(sec => {
                    if (sec.type === 'registry' && sec.content.cashFunds) {
                        return {
                            ...sec,
                            content: {
                                ...sec.content,
                                cashFunds: sec.content.cashFunds.map(f => 
                                    f.id === fund.id ? { ...f, currentAmount: (f.currentAmount || 0) + numAmount } : f
                                )
                            }
                        };
                    }
                    return sec;
                });
                
                await updateDoc(weddingRef, { sections: newSections });
                await addDoc(collection(db, `weddings/${weddingId}/contributions`), {
                    fundId: fund.id, fundTitle: fund.title, amount: numAmount, guestName: guestName.trim(), message: message.trim(), createdAt: serverTimestamp()
                });

                setTimeout(() => setStep('success'), 1500);
            }
        } catch (error) {
            console.error("Contribution failed:", error);
            alert("Payment failed to process. Please try again.");
            setStep('details');
        }
    };

    return (
        <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
                <div className="h-32 md:h-40 relative bg-gray-100">
                    <img src={fund.imageUrl || '[https://placehold.co/600x300/fdf2f8/db2777?text=Fund](https://placehold.co/600x300/fdf2f8/db2777?text=Fund)'} alt={fund.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <button onClick={onClose} className="absolute top-4 right-4 bg-black/30 hover:bg-black/50 backdrop-blur-md text-white w-8 h-8 rounded-full flex items-center justify-center transition">✕</button>
                    <div className="absolute bottom-4 left-5 text-white">
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-1">Contribute to</p>
                        <h3 className="font-serif text-2xl font-bold leading-tight">{fund.title}</h3>
                    </div>
                </div>

                <div className="p-6 md:p-8">
                    {step === 'details' && (
                        <form onSubmit={handleContribute} className="space-y-5 animate-fade-in">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Select Amount</label>
                                <div className="grid grid-cols-4 gap-2 mb-3">
                                    {presetAmounts.map(preset => (
                                        <button key={preset} type="button" onClick={() => setAmount(preset.toString())} className={`py-2 rounded-xl text-sm font-bold border-2 transition-all ${amount === preset.toString() ? 'border-transparent text-white shadow-md' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} style={amount === preset.toString() ? { backgroundColor: themeColor } : {}}>
                                            ${preset}
                                        </button>
                                    ))}
                                </div>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Other Amount" className="w-full pl-8 p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition font-bold text-gray-900" style={{ '--tw-ring-color': themeColor }} />
                                </div>
                            </div>
                            
                            <div className="border-t border-gray-100 pt-5 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Your Name</label>
                                    <input type="text" required value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="How should we address the card?" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': themeColor }} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Message (Optional)</label>
                                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Leave a warm wish for the couple..." rows="2" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition resize-none text-sm" style={{ '--tw-ring-color': themeColor }}></textarea>
                                </div>
                            </div>

                            <button type="submit" disabled={!amount || parseFloat(amount) <= 0 || !guestName} className="w-full py-4 rounded-xl font-bold text-white shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:transform-none text-lg mt-4" style={{ backgroundColor: themeColor }}>
                                Continue to Payment
                            </button>
                        </form>
                    )}

                    {step === 'processing' && (
                        <div className="py-12 flex flex-col items-center justify-center text-center animate-fade-in">
                            <div className="w-16 h-16 border-4 border-gray-200 border-t-pink-500 rounded-full animate-spin mb-6" style={{ borderTopColor: themeColor }}></div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Contribution</h3>
                            <p className="text-sm text-gray-500">Please wait while we secure your transaction securely...</p>
                        </div>
                    )}

                    {step === 'success' && (
                        <div className="py-8 text-center animate-fade-in">
                            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner" style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                                <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 font-serif mb-3">Thank You!</h3>
                            <p className="text-gray-600 mb-8 leading-relaxed">Your generous gift of <strong className="text-gray-900">${parseFloat(amount).toFixed(2)}</strong> has been successfully added to the {fund.title}.</p>
                            <button onClick={onClose} className="w-full py-3.5 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition shadow-md">
                                Return to Registry
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function RegistrySection({ content, theme, weddingId, db }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    const [selectedFund, setSelectedFund] = useState(null);

    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-5xl">
                <h2 className={`${sizes.heading} font-bold mb-6 font-serif text-center`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <p className={`${sizes.body} mb-12 max-w-2xl mx-auto leading-relaxed text-center ${content.textColor ? '' : 'text-gray-600'}`}>Your presence at our wedding is the greatest gift of all. However, if you wish to honor us with a gift, we have registered at the following stores and funds.</p>
                
                {(content.cashFunds || []).length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                        {content.cashFunds.map((fund, idx) => {
                            const current = fund.currentAmount || 0;
                            const goal = parseFloat(fund.goalAmount) || 0;
                            const progress = goal > 0 ? Math.min(100, (current / goal) * 100) : 0;

                            return (
                                <div key={fund.id || idx} className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden flex flex-col group hover:shadow-xl transition-shadow text-gray-900">
                                    <div className="h-48 relative overflow-hidden bg-gray-100">
                                        <img src={fund.imageUrl || '[https://placehold.co/600x400/fdf2f8/db2777?text=Fund](https://placehold.co/600x400/fdf2f8/db2777?text=Fund)'} alt={fund.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        <div className="absolute bottom-4 left-5 right-5">
                                            <h3 className={`font-serif font-bold text-white drop-shadow-md truncate ${sizes.cardHeading}`}>{fund.title}</h3>
                                        </div>
                                    </div>
                                    <div className="p-6 md:p-8 flex flex-col flex-grow">
                                        <p className="text-gray-600 mb-6 leading-relaxed flex-grow text-sm">{fund.description}</p>
                                        
                                        <div className="mb-6">
                                            <div className="flex justify-between items-end mb-2">
                                                <span className="text-2xl font-black text-gray-900">${current.toLocaleString()}</span>
                                                {goal > 0 && <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Goal: ${goal.toLocaleString()}</span>}
                                            </div>
                                            {goal > 0 && (
                                                <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${progress}%`, backgroundColor: theme.primaryColor }}></div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <button 
                                            onClick={() => setSelectedFund(fund)}
                                            className="w-full py-3.5 rounded-xl font-bold text-white shadow-md hover:opacity-90 transition transform hover:-translate-y-0.5" 
                                            style={{ backgroundColor: theme.primaryColor }}
                                        >
                                            Contribute
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {(content.physicalGifts || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center mb-12">
                        {content.physicalGifts.map((gift, idx) => (
                            <a key={idx} href={gift.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-gray-900">
                                <div className="h-48 relative overflow-hidden bg-white flex items-center justify-center p-4 border-b border-gray-50">
                                    {gift.imageUrl ? <img src={gift.imageUrl} alt={gift.title} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" /> : <span className="text-5xl">🎁</span>}
                                </div>
                                <div className="p-6 flex flex-col flex-grow text-center">
                                    <h3 className={`font-bold text-gray-900 mb-2 line-clamp-2 ${sizes.small}`}>{gift.title || 'Special Gift'}</h3>
                                    <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-4">{gift.storeName || 'Online Store'}</p>
                                    <div className="mt-auto flex flex-col gap-3">
                                        {gift.price && <p className="text-lg font-black text-gray-900">${gift.price}</p>}
                                        <span className="w-full py-3 rounded-xl font-bold text-white shadow-sm transition group-hover:opacity-90 text-sm" style={{ backgroundColor: theme.primaryColor }}>View Item</span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                )}

                {(content.links || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center">
                        {content.links.map((link, idx) => (
                            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 transform group flex flex-col items-center justify-center gap-4 text-gray-900">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-50 group-hover:bg-pink-50 transition-colors text-2xl" style={{ color: theme.primaryColor }}>🎁</div>
                                <span className={`font-bold text-gray-800 tracking-tight text-center ${sizes.cardHeading}`}>{link.storeName || 'Our Registry'}</span>
                                <span className={`${sizes.small} font-bold uppercase tracking-widest text-gray-400 group-hover:text-pink-500 transition-colors flex items-center gap-1`}>Shop Now <span className="group-hover:translate-x-1 transition-transform">→</span></span>
                            </a>
                        ))}
                    </div>
                )}

                {(!content.links || content.links.length === 0) && (!content.cashFunds || content.cashFunds.length === 0) && (!content.physicalGifts || content.physicalGifts.length === 0) && (
                    <div className="col-span-full py-12 text-gray-400 italic text-center">No registries added yet.</div>
                )}
            </div>

            {selectedFund && <CashFundContributionModal fund={selectedFund} theme={theme} onClose={() => setSelectedFund(null)} weddingId={weddingId} db={db} />}
        </div>
    );
}

export function TravelSection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-5xl">
                <h2 className={`${sizes.heading} font-bold text-center mb-12 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {(content.accommodations || []).map((acc, idx) => (
                        <div key={idx} className="bg-white p-8 md:p-10 rounded-3xl shadow-lg border border-gray-100 flex flex-col h-full relative overflow-hidden group text-gray-900">
                            <div className="absolute top-0 left-0 w-full h-2 transition-colors" style={{ backgroundColor: theme.primaryColor, opacity: 0.8 }}></div>
                            <h3 className={`${sizes.cardHeading} font-bold text-gray-900 mb-3 font-serif`} style={{ fontFamily: fontFam }}>{acc.name || 'Hotel Name'}</h3>
                            <p className={`${sizes.body} text-gray-500 mb-6 font-medium flex items-center gap-2`}><span className="text-xl opacity-80">📍</span> {acc.address || 'Location Address'}</p>
                            <p className={`${sizes.body} text-gray-600 leading-relaxed mb-8 flex-grow whitespace-pre-wrap`}>{acc.details || 'Details about the room block or travel logistics.'}</p>
                            {acc.bookingUrl && (
                                <a href={acc.bookingUrl} target="_blank" rel="noopener noreferrer" className={`inline-block w-full text-center py-4 rounded-full font-bold text-white transition-all transform hover:-translate-y-1 shadow-md hover:shadow-lg ${sizes.body}`} style={{ backgroundColor: theme.primaryColor }}>
                                    Book a Room
                                </a>
                            )}
                        </div>
                    ))}
                    {(!content.accommodations || content.accommodations.length === 0) && (
                        <div className="col-span-full py-12 text-center text-gray-400 italic">No travel details added yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function GallerySection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-6xl">
                <h2 className={`${sizes.heading} font-bold text-center font-serif mb-16`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
                    {(content.images || []).map((img, idx) => (
                        <div key={idx} className="break-inside-avoid overflow-hidden group relative shadow-md rounded-2xl">
                            <img src={img.url} alt="Gallery" className="w-full h-auto object-cover transform group-hover:scale-105 transition duration-700 ease-in-out bg-gray-200" />
                            {img.caption && (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex items-end justify-center p-6">
                                    <p className={`text-white text-center font-medium drop-shadow-md ${sizes.body}`}>{img.caption}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {(!content.images || content.images.length === 0) && (
                    <div className="text-center text-gray-400 italic">Upload images to display your gallery.</div>
                )}
            </div>
        </div>
    );
}

export function HeroSection({ content, theme, isEditing, onFieldChange }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    
    const backgroundStyle = content.imageUrl 
        ? { backgroundImage: `url(${content.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } 
        : { backgroundColor: content.bgColor || theme.backgroundColor || theme.primaryColor };
    
    const textColorVal = content.textColor || theme.primaryColor;
    const overlayColor = content.overlayColor || '#000000';
    const overlayOpacity = content.overlayOpacity !== undefined ? content.overlayOpacity : 0.4;
    
    const cardStyle = content.cardStyle || 'glass';
    let cardClasses = 'relative z-10 p-10 md:p-16 max-w-4xl w-full mx-4 animate-fade-in-up';
    if (cardStyle === 'glass') {
        cardClasses += ' bg-white/95 backdrop-blur-md rounded-[2.5rem] shadow-2xl border border-white/50';
    } else if (cardStyle === 'solid') {
        cardClasses += ' bg-white rounded-[2.5rem] shadow-xl border border-gray-100';
    }

    const hAlign = content.horizontalAlign || 'center';
    const vAlign = content.verticalAlign || 'center';

    const justifyClass = hAlign === 'left' ? 'justify-start' : hAlign === 'right' ? 'justify-end' : 'justify-center';
    const itemsClass = vAlign === 'top' ? 'items-start pt-16 md:pt-24' : vAlign === 'bottom' ? 'items-end pb-16 md:pb-24' : 'items-center py-24';
    const textAlignClass = hAlign === 'left' ? 'text-left' : hAlign === 'right' ? 'text-right' : 'text-center';
    const lineAlign = hAlign === 'left' ? 'ml-0' : hAlign === 'right' ? 'mr-0' : 'mx-auto';

    return (
        <div className={`relative flex ${itemsClass} ${justifyClass} ${textAlignClass} min-h-[70vh] md:min-h-[85vh] px-4 overflow-hidden border-b border-gray-100`} style={backgroundStyle}>
            {content.imageUrl && <div className="absolute inset-0" style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}></div>}
            <div className={cardClasses}>
                {isEditing ? (
                    <div className="space-y-6">
                        <input type="text" value={content.title || ''} onChange={(e) => onFieldChange('title', e.target.value)} className={`${sizes.heroHeading} font-bold bg-transparent w-full outline-none border-b-2 border-dashed placeholder-gray-400 ${textAlignClass}`} style={{ fontFamily: fontFam, color: textColorVal, borderColor: textColorVal }} placeholder="Couple Names" />
                        <input type="date" value={content.date || ''} onChange={(e) => onFieldChange('date', e.target.value)} className={`${sizes.heroSub} mt-4 bg-transparent w-full outline-none border-b-2 border-dashed tracking-widest uppercase font-semibold ${textAlignClass}`} style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563'), borderColor: content.textColor || '#d1d5db' }} />
                        <input type="text" value={content.city || ''} onChange={(e) => onFieldChange('city', e.target.value)} className={`${sizes.heroSub} mt-4 bg-transparent w-full outline-none border-b-2 border-dashed tracking-widest uppercase font-semibold ${textAlignClass}`} style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563'), borderColor: content.textColor || '#d1d5db' }} placeholder="City, State (Optional)" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        <h1 className={`${sizes.heroHeading} font-bold tracking-tight leading-tight`} style={{ fontFamily: fontFam, color: textColorVal }}>{content.title}</h1>
                        <div className={`h-px w-24 my-6 ${lineAlign} ${cardStyle === 'transparent' ? 'bg-current opacity-50' : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'}`}></div>
                        <p className={`${sizes.heroSub} tracking-widest uppercase font-semibold`} style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563') }}>
                            {content.date ? new Date(content.date.replace(/-/g, '\/')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Date TBD'}
                            {content.city && <span className="block mt-2">{content.city}</span>}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export function StorySection({ content, theme, isEditing, onFieldChange }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
     return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-6xl">
                <div className={`flex flex-col md:flex-row items-center gap-12 lg:gap-20 ${content.imageUrl ? '' : 'justify-center text-center'}`}>
                    {content.imageUrl && (
                        <div className="w-full md:w-1/2">
                            <div className="relative p-4 max-w-md mx-auto md:max-w-none">
                                <div className="absolute inset-0 border-4 translate-x-4 translate-y-4 rounded-2xl transition-all" style={{ borderColor: content.textColor || theme.primaryColor, opacity: 0.4 }}></div>
                                <img src={content.imageUrl} alt="Our Story" className="relative w-full h-auto object-cover rounded-2xl shadow-xl z-10 aspect-[3/4]" />
                            </div>
                        </div>
                    )}
                    <div className={`w-full ${content.imageUrl ? 'md:w-1/2' : 'md:w-3/4'} space-y-8`}>
                        {isEditing ? (
                            <input type="text" value={content.heading || ''} onChange={(e) => onFieldChange('heading', e.target.value)} className={`${sizes.heading} font-bold mb-6 w-full bg-transparent outline-none border-b-2 border-dashed`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor, borderColor: content.textColor || theme.primaryColor }} placeholder="Heading" />
                        ) : (
                            <h2 className={`${sizes.heading} font-bold font-serif leading-tight`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                        )}
                        
                        {isEditing ? (
                            <textarea value={content.text || ''} onChange={(e) => onFieldChange('text', e.target.value)} className={`text-gray-700 whitespace-pre-wrap leading-relaxed w-full h-64 p-4 border rounded-xl focus:ring-2 outline-none ${sizes.body}`} style={content.textColor ? { color: content.textColor, backgroundColor: 'rgba(255,255,255,0.8)' } : {}} placeholder="Write your story..." />
                        ) : (
                            <p className={`whitespace-pre-wrap leading-relaxed font-light opacity-90 ${content.textColor ? '' : 'text-gray-700'} ${sizes.body}`} style={content.textColor ? { color: content.textColor } : {}}>{content.text}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
     );
}

export function EventsSection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100 relative overflow-hidden" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-black opacity-5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
            
            <div className="container mx-auto max-w-5xl relative z-10">
                <h2 className={`${sizes.heading} font-bold text-center mb-16 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                
                <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                    {(content.eventsList || []).map((evt, idx) => (
                        <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" style={{ color: theme.primaryColor }}>
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path></svg>
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:-translate-y-1 transition transform duration-300 text-gray-900">
                                <p className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: theme.primaryColor }}>{evt.time}</p>
                                <h3 className={`${sizes.cardHeading} font-bold mb-2 font-serif`} style={{ fontFamily: fontFam }}>{evt.title}</h3>
                                <p className={`${sizes.small} text-gray-500 font-medium mb-3 flex items-center gap-1.5`}><span className="opacity-70">📍</span> {evt.location}</p>
                                <p className={`${sizes.body} text-gray-600 leading-relaxed`}>{evt.description}</p>
                            </div>
                        </div>
                    ))}
                    {(!content.eventsList || content.eventsList.length === 0) && (
                        <div className="text-center py-12 text-gray-400 italic">No events scheduled yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function RsvpSection({ content, theme, weddingId, isEditing, db }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    
    const [partyMembers, setPartyMembers] = useState([{ id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }]);
    const [submitted, setSubmitted] = useState(false);

    const isPastDeadline = content.deadline && new Date(content.deadline) < new Date(new Date().setHours(0,0,0,0));
    const maxPartySize = content.maxPartySize || 10;
    const canAddMore = partyMembers.length < maxPartySize;

    const handleAddMember = () => setPartyMembers([...partyMembers, { id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }]);
    const handleMemberChange = (id, field, value) => setPartyMembers(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    const handleRemoveMember = (id) => setPartyMembers(prev => prev.filter(m => m.id !== id));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (partyMembers.some(m => !m.name.trim())) return;
        
        await addDoc(collection(db, `weddings/${weddingId}/rsvps`), {
            partyMembers: partyMembers.map(m => ({
                name: m.name.trim(), email: m.email ? m.email.trim() : '', isAttending: m.isAttending === 'yes', mealChoice: m.mealChoice || '', dietary: m.dietary || ''
            })),
            submittedAt: serverTimestamp()
        });
        setSubmitted(true);
    };

    return (
        <div className="py-24 border-t-8 border-transparent" style={{ backgroundColor: content.bgColor || theme.backgroundColor, borderTopColor: theme.primaryColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto p-4 md:p-8 max-w-2xl bg-white shadow-2xl rounded-3xl border border-gray-100 relative overflow-hidden text-gray-900">
                <div className="absolute top-0 left-0 w-full h-2" style={{ backgroundColor: theme.primaryColor }}></div>
                <h2 className={`${sizes.heading} font-bold text-center mb-8 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                {submitted ? (
                    <div className="text-center py-12 animate-fade-in">
                        <div className="text-5xl mb-4">✨</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>Thank you!</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>Your response has been received.</p>
                    </div>
                ) : isPastDeadline && !isEditing ? (
                    <div className="text-center py-12 animate-fade-in bg-gray-50 rounded-2xl border border-gray-200 m-4">
                        <div className="text-5xl mb-4">⌛</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>RSVPs are closed</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>The deadline to RSVP was {new Date(content.deadline).toLocaleDateString()}. Please contact the host directly if you need to make changes.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-6">
                            {partyMembers.map((member, index) => (
                                <div key={member.id} className="bg-gray-50 p-5 rounded-2xl border border-gray-200 relative animate-fade-in">
                                    {index > 0 && <button type="button" onClick={() => handleRemoveMember(member.id)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 font-bold text-lg leading-none transition-colors">✕</button>}
                                    <h4 className={`font-bold text-gray-800 mb-4 uppercase tracking-wider ${sizes.small}`}>Guest {index + 1}</h4>
                                    <div className="space-y-4">
                                        <input type="text" required value={member.name} onChange={e => handleMemberChange(member.id, 'name', e.target.value)} placeholder="Full Name *" className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} style={{ '--tw-ring-color': theme.primaryColor }} />
                                        <input type="email" value={member.email} onChange={e => handleMemberChange(member.id, 'email', e.target.value)} placeholder="Email Address (Optional)" className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} style={{ '--tw-ring-color': theme.primaryColor }} />
                                        <div className="flex gap-4 items-center">
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'yes'} onChange={() => handleMemberChange(member.id, 'isAttending', 'yes')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" /><span className={`font-medium ${sizes.body}`}>Joyfully Accept</span></label>
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'no'} onChange={() => handleMemberChange(member.id, 'isAttending', 'no')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" /><span className={`font-medium text-gray-600 ${sizes.body}`}>Regretfully Decline</span></label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {canAddMore && (
                            <div className="pt-2">
                                <button type="button" onClick={handleAddMember} className="font-bold flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed bg-gray-50 hover:bg-gray-100 transition-colors text-gray-600" style={{ borderColor: theme.primaryColor, color: theme.primaryColor }}>
                                    <span className="text-xl leading-none">+</span> Add another guest to this RSVP
                                </button>
                            </div>
                        )}
                        <button type="submit" className={`w-full text-white py-4 rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-1 transition-all mt-6 shadow-md ${sizes.body}`} style={{backgroundColor: theme.primaryColor}}>Submit RSVP</button>
                    </form>
                )}
            </div>
        </div>
    );
}

export function SaveTheDateModal({ siteData, onClose, db }) {
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', additionalGuests: 0, address1: '', address2: '', city: '', state: '', zip: '', country: 'US' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const theme = siteData.theme || {};
    const primaryColor = theme.primaryColor || '#ec4899';
    const fontFamily = theme.fontFamily || 'inherit';

    const heroSection = siteData.sections.find(s => s.type === 'hero')?.content || {};
    const eventDate = heroSection.date || '';
    const eventTitle = siteData.title || 'Our Wedding';
    const heroImage = heroSection.imageUrl || null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'additionalGuests' ? (parseInt(value) || 0) : value }));
    };

    const getCalendarLinks = () => {
        if (!eventDate) return { google: '', ics: '', icsContent: '', yahoo: '' };
        const start = new Date(`${eventDate}T12:00:00`);
        const end = new Date(`${eventDate}T12:00:00`);
        end.setDate(end.getDate() + 1); 

        const formatDate = (d) => d.toISOString().replace(/-|:|\.\d\d\d/g, "").substring(0, 8);
        const startStr = formatDate(start);
        const endStr = formatDate(end);
        
        const details = `Please save the date for ${eventTitle}! Formal invitation to follow.`;
        
        const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startStr}/${endStr}&details=${encodeURIComponent(details)}`;
        const yahoo = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(eventTitle)}&st=${startStr}&et=${endStr}&desc=${encodeURIComponent(details)}`;
        
        const icsContent = [
            'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
            `DTSTART;VALUE=DATE:${startStr}`, `DTEND;VALUE=DATE:${endStr}`,
            `SUMMARY:${eventTitle}`, `DESCRIPTION:${details}`,
            'END:VEVENT', 'END:VCALENDAR'
        ].join('\n');
        
        const ics = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;
        return { google, ics, icsContent, yahoo };
    };

    const links = getCalendarLinks();

    const handleIcsClick = (e) => {
        e.preventDefault();
        const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
        const isAppleDevice = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

        if (isAppleDevice && isSafari) {
             window.location.href = links.ics;
        } else {
             const blob = new Blob([links.icsContent], { type: 'text/calendar;charset=utf-8' });
             const url = window.URL.createObjectURL(blob);
             const link = document.createElement('a');
             link.href = url;
             link.setAttribute('download', 'save_the_date.ics');
             document.body.appendChild(link);
             link.click();
             document.body.removeChild(link);
             setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.firstName || !formData.lastName || !formData.email) return;
        setLoading(true);
        try {
            await addDoc(collection(db, `weddings/${siteData.id}/saveTheDates`), {
                ...formData,
                createdAt: serverTimestamp()
            });
            setSubmitted(true);
        } catch(err) {
            console.error("Save The Date Error:", err);
            alert("Failed to submit details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 sm:p-6 animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden relative min-h-[500px] md:h-auto my-8 md:my-0">
                <button onClick={onClose} className="absolute top-3 right-3 md:top-4 md:right-4 z-[60] bg-white/90 hover:bg-white backdrop-blur-md rounded-full w-10 h-10 flex items-center justify-center text-gray-900 transition-colors shadow-md font-bold border border-gray-200 text-xl">×</button>
                
                <div className="w-full md:w-2/5 bg-gray-100 relative flex-shrink-0 min-h-[250px] md:min-h-full">
                    {heroImage ? (
                        <img src={heroImage} alt="Event Theme" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <div className="absolute inset-0 w-full h-full" style={{ backgroundColor: primaryColor, opacity: 0.2 }}></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-8 text-white">
                        <h3 className="text-3xl font-bold font-serif mb-2" style={{ fontFamily }}>{eventTitle}</h3>
                        {eventDate && <p className="text-lg font-medium opacity-90">{new Date(eventDate.replace(/-/g, '/')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                        <p className="text-xs opacity-70 mt-2 uppercase tracking-widest font-bold">Save the Date</p>
                    </div>
                </div>

                <div className="w-full md:w-3/5 p-6 md:p-10 bg-white flex flex-col justify-center">
                    {submitted ? (
                        <div className="text-center animate-fade-in space-y-6 py-8">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto text-4xl text-green-500 shadow-inner">
                                <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h3 className="text-3xl font-bold text-gray-900 font-serif mb-2" style={{ fontFamily }}>Thank you!</h3>
                                <p className="text-gray-600">We've securely saved your details. Formal invitation to follow.</p>
                            </div>
                            
                            {eventDate && (
                                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 text-left mt-6">
                                    <h4 className="font-bold text-gray-800 mb-4 uppercase tracking-wider text-xs text-center">Add to your calendar</h4>
                                    <div className="flex flex-col gap-3">
                                        <a href={links.google} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/><path fill="none" d="M1 1h22v22H1z"/></svg>
                                            Google Calendar
                                        </a>
                                        <button onClick={handleIcsClick} className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.3.87.78 0 2.26-1.07 3.81-.91 1.65.17 3.05.8 3.94 2.1-3.21 1.9-2.74 6.47.53 7.8-.73 1.1-1.46 2.17-2.43 3.51M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25"/></svg>
                                            Apple / Outlook
                                        </button>
                                        <a href={links.yahoo} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M22.46 4.98c-.1-.13-.26-.2-.42-.2H18.8c-.28 0-.48.26-.39.52.88 2.55 1.5 5.56 1.5 8.7 0 2.15-.31 4.19-.88 6.07-.09.28.12.56.41.56h3.19c.16 0 .32-.07.41-.21A14.93 14.93 0 0 0 24 14c0-3.23-.55-6.3-1.54-9.02zM12 24c-1.35 0-2.65-.18-3.88-.51-.27-.08-.55.07-.63.35-.38 1.34-1.22 2.45-2.36 3.19-.2.13-.17.43.05.5A11.96 11.96 0 0 0 12 24zm-6.68-1.57c.21-.19.25-.5.06-.72A11.95 11.95 0 0 1 1.5 14c0-3.3 1.34-6.28 3.5-8.5.18-.19.16-.49-.03-.66l-2.44-2.1c-.16-.14-.41-.12-.55.04C.67 5.43 0 9.54 0 14c0 3.86 1.56 7.36 4.1 9.88.16.16.4.15.54-.01l.68-1.44zm6.68-20.43c1.35 0 2.65.18 3.88.51.27.07.55-.08.63-.35.38-1.34 1.22-2.45 2.36-3.19.2-.13.17-.43-.05-.5A11.96 11.96 0 0 0 12 0zm6.68 1.57c-.21.19-.25.5-.06.72A11.95 11.95 0 0 1 22.5 10c0 3.3-1.34 6.28-3.5 8.5-.18.19-.16.49.03.66l2.44 2.1c.16.14.41.12.55-.04A14.93 14.93 0 0 0 24 10c0-3.86-1.56-7.36-4.1-9.88-.16-.16-.4-.15-.54.01l-.68 1.44zM12 4.5c-5.24 0-9.5 4.26-9.5 9.5s4.26 9.5 9.5 9.5 9.5-4.26 9.5-9.5-4.26-9.5-9.5-9.5zm0 17C7.86 21.5 4.5 18.14 4.5 14S7.86 6.5 12 6.5 19.5 9.86 19.5 14 16.14 21.5 12 21.5z"/></svg>
                                            Yahoo Calendar
                                        </a>
                                    </div>
                                </div>
                            )}
                            <button onClick={onClose} className="mt-6 text-gray-500 hover:text-gray-900 font-bold transition underline text-sm">Return to Website</button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif" style={{ fontFamily }}>Let's stay in touch!</h3>
                                <p className="text-sm text-gray-600">Please provide your mailing address and email so we can send you updates and a formal invitation.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">First Name *</label>
                                        <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Last Name *</label>
                                        <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Email Address *</label>
                                        <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Phone Number</label>
                                        <PhoneInput name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                {siteData.allowAdditionalGuests && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Additional Guests (+{siteData.maxAdditionalGuests || 1} Max)</label>
                                        <input type="number" name="additionalGuests" min="0" max={siteData.maxAdditionalGuests || 1} value={formData.additionalGuests} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                )}

                                <div className="pt-3 border-t border-gray-100">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Mailing Address (For Formal Invite)</label>
                                    <div className="space-y-2">
                                        <input type="text" name="address1" placeholder="Street Address" value={formData.address1} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <input type="text" name="address2" placeholder="Apt, Suite, Unit (optional)" value={formData.address2} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <div className="grid grid-cols-3 gap-2">
                                            <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleChange} className="col-span-3 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="state" placeholder="State" value={formData.state} onChange={handleChange} className="col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="zip" placeholder="Zip Code" value={formData.zip} onChange={handleChange} className="col-span-2 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        </div>
                                    </div>
                                </div>
                                
                                <SmsComplianceCheckboxes />

                                <div className="pt-4 flex flex-col gap-2">
                                    <button type="submit" disabled={loading} className="w-full py-4 rounded-xl font-bold text-white shadow-md hover:opacity-90 transform hover:-translate-y-0.5 transition-all disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                                        {loading ? 'Saving...' : 'Submit & Save Date'}
                                    </button>
                                    <button type="button" onClick={onClose} className="w-full py-3 rounded-xl font-bold text-gray-500 hover:text-gray-800 transition text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function WeddingSitePublicView({ subdomain, isAuthReady }) {
    const [siteData, setSiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSaveTheDate, setShowSaveTheDate] = useState(false);
    
    const db = getFirestore();

    useEffect(() => {
        // Need auth here to verify firebase functionality locally. In standard next.js server it could be different, but in this setup we rely on anon users for reading.
        // Assuming the auth flow is handled from the app wrapper, we just run the query.
        
        const fetchSite = async () => {
            const q = query(collection(db, "weddings"), where("subdomain", "==", subdomain), limit(1));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const siteDoc = querySnapshot.docs[0];
                const data = siteDoc.data();
                if (data.isPublished) {
                    setSiteData({ id: siteDoc.id, ...data });
                } else {
                    setError("This website is not yet published.");
                }
            } else {
                setError("Oops! We couldn't find a wedding website at this address.");
            }
            setLoading(false);
        };
        fetchSite();
    }, [subdomain, isAuthReady]);

    useEffect(() => {
        if (!siteData) return;
        const fontsToLoad = new Set();
        if (siteData.theme?.fontFamily) fontsToLoad.add(siteData.theme.fontFamily);
        (siteData.sections || []).forEach(sec => {
            if (sec.content?.fontFamily) fontsToLoad.add(sec.content.fontFamily);
        });

        fontsToLoad.forEach(fontFamily => {
            const font = fontFamily.replace(/'/g, "").split(',')[0].trim().replace(/ /g, "+");
            if (!font) return;
            const linkId = `google-font-${font.toLowerCase()}`;
            let link = document.getElementById(linkId);
            if (!link) {
                link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                document.head.appendChild(link);
            }
            link.href = `https://fonts.googleapis.com/css?family=${font}:400,700&display=swap`;
        });
    }, [siteData?.theme?.fontFamily, siteData?.sections]);

    const renderSection = (section, index) => {
        if (!section.enabled) return null;
        const props = { content: section.content, theme: siteData.theme, isEditing: false, weddingId: siteData.id, db };

        const animationClass = siteData.theme?.enableAnimations ? 'wedding-animate-fade-in' : '';
        const animationStyle = siteData.theme?.enableAnimations ? { animationFillMode: 'both', animationDuration: '0.8s', animationDelay: `${index * 150}ms` } : {};

        let SectionComponent = null;
        switch (section.type) {
            case 'hero': SectionComponent = <HeroSection key={section.id} {...props} />; break;
            case 'story': SectionComponent = <StorySection key={section.id} {...props} />; break;
            case 'events': SectionComponent = <EventsSection key={section.id} {...props} />; break;
            case 'travel': SectionComponent = <TravelSection key={section.id} {...props} />; break;
            case 'rsvp': SectionComponent = <RsvpSection key={section.id} {...props} />; break;
            case 'gallery': SectionComponent = <GallerySection key={section.id} {...props} />; break;
            case 'registry': SectionComponent = <RegistrySection key={section.id} {...props} />; break;
            default: return null;
        }

        return (
            <div key={`wrap-${section.id}-${siteData.theme?.enableAnimations ? 'anim' : 'no'}`} className={animationClass} style={animationStyle}>
                {SectionComponent}
            </div>
        );
    };

    if (loading) return <div className="text-center p-20">Loading Wedding Site...</div>;
    if (error) return <div className="text-center p-20 text-xl">{error}</div>;

    return (
        <div className={`relative ${siteData.enableSaveTheDate ? 'pb-24 md:pb-20' : ''}`}>
            <style dangerouslySetInnerHTML={{__html: `.wedding-animate-fade-in { animation: weddingFadeIn 0.8s ease-out forwards; } @keyframes weddingFadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }`}} />

            <nav className="sticky top-0 z-[100] w-full bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm transition-all duration-300">
                <div className="container mx-auto px-4 md:px-8">
                    <ul className="flex items-center justify-start md:justify-center gap-6 md:gap-8 overflow-x-auto py-3.5 text-xs md:text-sm font-bold uppercase tracking-wider [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {siteData.sections.filter(s => s.enabled).map(section => {
                            let linkText = section.content.heading || section.content.title || section.type;
                            if (section.type === 'hero') linkText = 'Home';
                            
                            return (
                                <li key={`nav-${section.id}`} className="flex-shrink-0">
                                    <button 
                                        onClick={() => {
                                            const el = document.getElementById(section.id);
                                            if (el) {
                                                const y = el.getBoundingClientRect().top + window.scrollY - 55;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }}
                                        className="text-gray-600 hover:text-gray-900 transition-colors"
                                        onMouseEnter={(e) => e.target.style.color = siteData.theme?.primaryColor || '#ec4899'}
                                        onMouseLeave={(e) => e.target.style.color = ''}
                                    >
                                        {linkText}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </nav>

            {siteData.sections.map((section, index) => renderSection(section, index))}
            
            {siteData.enableSaveTheDate && (
                <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-gray-200 p-4 md:px-8 z-40 flex justify-between items-center shadow-[0_-10px_30px_rgba(0,0,0,0.1)] animate-fade-in-up">
                    <div className="hidden md:block">
                        <p className="font-bold text-gray-900 text-lg leading-tight">Save our Date!</p>
                        <p className="text-sm text-gray-600">Please share your mailing address so we can send you a formal invitation.</p>
                    </div>
                    <button onClick={() => setShowSaveTheDate(true)} className="w-full md:w-auto px-8 py-3 rounded-full font-bold text-white shadow-lg hover:opacity-90 transition-all transform hover:-translate-y-0.5" style={{ backgroundColor: siteData.theme?.primaryColor || '#ec4899' }}>
                        Add Details & Save the Date
                    </button>
                </div>
            )}
            
            {showSaveTheDate && <SaveTheDateModal siteData={siteData} onClose={() => setShowSaveTheDate(false)} db={db} />}
        </div>
    );
}